import { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
	BanknotesIcon,
	CheckCircleIcon,
	ArrowTrendingUpIcon,
	ArrowTrendingDownIcon,
	FunnelIcon,
	XMarkIcon,
	ChevronDownIcon,
	DocumentArrowDownIcon,
	ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AppLayout from '../../components/layout/AppLayout';
import { Spinner } from '../../components/ui';
import { staffEarningsApi } from '../../api';
import type {
	StaffEarningsPeriod,
	StaffEarningsResponse,
	StaffEarningsStatus,
} from '../../types/staffEarnings';

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string =>
	'$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const formatPct = (pct: number, digits = 1): string => `${pct.toFixed(digits)}%`;

const PAGE_SIZE = 10;

const PERIODS: { key: StaffEarningsPeriod; label: string }[] = [
	{ key: 'WEEK', label: 'This Week' },
	{ key: 'MONTH', label: 'This Month' },
	{ key: 'YEAR', label: 'Year' },
];

const STATUS_OPTIONS: { key: StaffEarningsStatus; label: string }[] = [
	{ key: 'PAID', label: 'Paid' },
	{ key: 'PENDING', label: 'Pending' },
];

// ─── Status Badge ───────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => {
	const normalized = status.toUpperCase();
	if (normalized === 'PAID') {
		return (
			<span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
				Paid
			</span>
		);
	}
	if (normalized === 'PENDING') {
		return (
			<span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
				Pending
			</span>
		);
	}
	return (
		<span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
			{status}
		</span>
	);
};

// ─── Stat Cards ─────────────────────────────────────────────────

const TotalEarningsCard = ({ value, changePct }: { value: number; changePct?: number }) => (
	<div
		className="relative overflow-hidden rounded-xl p-5 shadow-sm text-white"
		style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
	>
		<div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3">
			<BanknotesIcon className="w-6 h-6 text-white" />
		</div>
		<div className="text-xs font-semibold text-white/80 uppercase tracking-wide mb-1">
			Total Earnings
		</div>
		<div className="text-2xl font-extrabold leading-none mb-2.5">{formatCurrency(value)}</div>
		{changePct != null ? (
			<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 text-xs font-semibold">
				{changePct >= 0 ? (
					<ArrowTrendingUpIcon className="w-3.5 h-3.5" />
				) : (
					<ArrowTrendingDownIcon className="w-3.5 h-3.5" />
				)}
				{formatPct(changePct)} vs last period
			</span>
		) : (
			<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 text-xs font-semibold">
				—
			</span>
		)}
	</div>
);

const ServicesCompletedCard = ({ value, delta }: { value: number; delta?: number }) => {
	const hasDelta = delta != null && delta !== 0;
	const direction = delta != null && delta >= 0 ? 'up' : 'down';
	return (
		<div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm">
			<div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
				<CheckCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
			</div>
			<div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
				Services Completed
			</div>
			<div className="text-2xl font-extrabold leading-none text-gray-900 dark:text-white mb-2.5">
				{value}
			</div>
			{hasDelta ? (
				<span
					className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
						direction === 'up'
							? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
							: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
					}`}
				>
					{direction === 'up' ? (
						<ArrowTrendingUpIcon className="w-3.5 h-3.5" />
					) : (
						<ArrowTrendingDownIcon className="w-3.5 h-3.5" />
					)}
					{Math.abs(delta as number)} {direction === 'up' ? 'more' : 'fewer'}
				</span>
			) : (
				<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400">
					No change
				</span>
			)}
		</div>
	);
};

// ─── Filter Field ───────────────────────────────────────────────

const FilterField = ({ label, children }: { label: string; children: ReactNode }) => (
	<div className="flex flex-col gap-1 flex-1 min-w-[160px]">
		<label className="text-[0.7rem] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
			{label}
		</label>
		{children}
	</div>
);

// ─── Pagination ─────────────────────────────────────────────────

const Pagination = ({
	currentPage,
	totalPages,
	totalElements,
	pageSize,
	onPageChange,
}: {
	currentPage: number;
	totalPages: number;
	totalElements: number;
	pageSize: number;
	onPageChange: (page: number) => void;
}) => {
	const pages = useMemo(() => {
		const result: (number | 'dots')[] = [];
		if (totalPages <= 7) {
			for (let i = 0; i < totalPages; i++) result.push(i);
		} else {
			result.push(0);
			if (currentPage > 2) result.push('dots');
			for (
				let i = Math.max(1, currentPage - 1);
				i <= Math.min(totalPages - 2, currentPage + 1);
				i++
			) {
				result.push(i);
			}
			if (currentPage < totalPages - 3) result.push('dots');
			result.push(totalPages - 1);
		}
		return result;
	}, [currentPage, totalPages]);

	if (totalPages <= 1) return null;

	const start = currentPage * pageSize + 1;
	const end = Math.min((currentPage + 1) * pageSize, totalElements);

	return (
		<div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
			<button
				onClick={() => onPageChange(currentPage - 1)}
				disabled={currentPage === 0}
				className="w-9 h-9 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
			>
				&lsaquo;
			</button>
			{pages.map((page, idx) =>
				page === 'dots' ? (
					<span key={`dots-${idx}`} className="px-2 text-gray-400 dark:text-gray-500">
						...
					</span>
				) : (
					<button
						key={page}
						onClick={() => onPageChange(page)}
						className={`w-9 h-9 rounded-lg text-sm font-medium flex items-center justify-center transition-colors ${
							page === currentPage
								? 'text-white border border-transparent'
								: 'border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:border-indigo-500 hover:text-indigo-600'
						}`}
						style={
							page === currentPage
								? { background: 'linear-gradient(135deg, #667eea, #764ba2)' }
								: undefined
						}
					>
						{page + 1}
					</button>
				)
			)}
			<button
				onClick={() => onPageChange(currentPage + 1)}
				disabled={currentPage === totalPages - 1}
				className="w-9 h-9 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
			>
				&rsaquo;
			</button>
			<span className="ml-3 text-xs text-gray-500 dark:text-gray-400">
				Showing {start}-{end} of {totalElements}
			</span>
		</div>
	);
};

// ─── Main Component ─────────────────────────────────────────────

const Earnings = () => {
	// Applied filter state
	const [period, setPeriod] = useState<StaffEarningsPeriod>('MONTH');
	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');
	const [service, setService] = useState('');
	const [status, setStatus] = useState<StaffEarningsStatus | ''>('');
	const [page, setPage] = useState(0);

	// Draft filter state (in collapsible panel)
	const [draftDateFrom, setDraftDateFrom] = useState('');
	const [draftDateTo, setDraftDateTo] = useState('');
	const [draftService, setDraftService] = useState('');
	const [draftStatus, setDraftStatus] = useState<StaffEarningsStatus | ''>('');

	const [filterOpen, setFilterOpen] = useState(false);
	const [data, setData] = useState<StaffEarningsResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const usingCustomDateRange = Boolean(dateFrom || dateTo);
	const appliedFilterCount =
		(dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (service ? 1 : 0) + (status ? 1 : 0);

	const fetchEarnings = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const response = await staffEarningsApi.getEarnings({
				period: usingCustomDateRange ? undefined : period,
				dateFrom: dateFrom || undefined,
				dateTo: dateTo || undefined,
				service: service || undefined,
				status: status || undefined,
				page,
				size: PAGE_SIZE,
			});
			setData(response);
		} catch {
			setError('Failed to load earnings. Please try again.');
		} finally {
			setIsLoading(false);
		}
	}, [period, dateFrom, dateTo, service, status, page, usingCustomDateRange]);

	useEffect(() => {
		fetchEarnings();
	}, [fetchEarnings]);

	const handleSelectPeriod = (next: StaffEarningsPeriod) => {
		setPeriod(next);
		setPage(0);
	};

	const handleApplyFilters = () => {
		setDateFrom(draftDateFrom);
		setDateTo(draftDateTo);
		setService(draftService);
		setStatus(draftStatus);
		setPage(0);
		setFilterOpen(false);
	};

	const handleClearFilters = () => {
		setDraftDateFrom('');
		setDraftDateTo('');
		setDraftService('');
		setDraftStatus('');
		setDateFrom('');
		setDateTo('');
		setService('');
		setStatus('');
		setPage(0);
	};

	const handleDownloadPdf = () => {
		toast('PDF export will be enabled soon.', { icon: '📄' });
	};

	// ─── Render states ─────────────────────────────────────────

	if (isLoading && !data) {
		return (
			<AppLayout>
				<div className="flex items-center justify-center py-20">
					<Spinner size="lg" />
				</div>
			</AppLayout>
		);
	}

	if (error && !data) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<p className="text-red-500 dark:text-red-400">{error}</p>
					<button
						onClick={fetchEarnings}
						className="px-4 py-2 rounded-lg font-semibold text-sm text-white"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						Retry
					</button>
				</div>
			</AppLayout>
		);
	}

	const availableServices = data?.availableServices ?? [];
	const transactions = data?.transactions ?? [];

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex flex-wrap items-start justify-between gap-4 mb-6">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
						My Earnings
					</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						Track your income and commission history
					</p>
				</div>
				<div className="flex bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
					{PERIODS.map(p => {
						const isActive = !usingCustomDateRange && p.key === period;
						return (
							<button
								key={p.key}
								onClick={() => handleSelectPeriod(p.key)}
								className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
									isActive
										? 'text-white'
										: 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400'
								}`}
								style={
									isActive
										? {
												background:
													'linear-gradient(135deg, #667eea, #764ba2)',
											}
										: undefined
								}
							>
								{p.label}
							</button>
						);
					})}
				</div>
			</div>

			{/* Filter Panel */}
			<div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mb-5">
				<button
					type="button"
					onClick={() => setFilterOpen(o => !o)}
					className="w-full flex items-center justify-between px-5 py-3.5"
				>
					<div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-200">
						<FunnelIcon className="w-4 h-4" />
						Customise Filters
						{appliedFilterCount > 0 && (
							<span
								className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[0.65rem] font-extrabold text-white"
								style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
							>
								{appliedFilterCount}
							</span>
						)}
					</div>
					<div className="flex items-center gap-3">
						{appliedFilterCount > 0 && (
							<span
								onClick={e => {
									e.stopPropagation();
									handleClearFilters();
								}}
								className="text-xs font-semibold text-red-500 hover:text-red-600 cursor-pointer"
							>
								Clear all
							</span>
						)}
						<ChevronDownIcon
							className={`w-4 h-4 text-gray-400 transition-transform ${
								filterOpen ? 'rotate-180' : ''
							}`}
						/>
					</div>
				</button>

				{filterOpen && (
					<div className="border-t border-gray-100 dark:border-gray-700 px-5 py-4 flex flex-wrap items-end gap-4">
						<FilterField label="Date From">
							<input
								type="date"
								value={draftDateFrom}
								onChange={e => setDraftDateFrom(e.target.value)}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							/>
						</FilterField>
						<FilterField label="Date To">
							<input
								type="date"
								value={draftDateTo}
								onChange={e => setDraftDateTo(e.target.value)}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							/>
						</FilterField>
						<FilterField label="Service">
							<select
								value={draftService}
								onChange={e => setDraftService(e.target.value)}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							>
								<option value="">All Services</option>
								{availableServices.map(s => (
									<option key={s} value={s}>
										{s}
									</option>
								))}
							</select>
						</FilterField>
						<FilterField label="Status">
							<select
								value={draftStatus}
								onChange={e =>
									setDraftStatus(e.target.value as StaffEarningsStatus | '')
								}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							>
								<option value="">All Status</option>
								{STATUS_OPTIONS.map(o => (
									<option key={o.key} value={o.key}>
										{o.label}
									</option>
								))}
							</select>
						</FilterField>
						<button
							type="button"
							onClick={handleApplyFilters}
							className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 whitespace-nowrap"
							style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
						>
							Apply Filters
						</button>
					</div>
				)}

				{appliedFilterCount > 0 && (
					<div className="border-t border-blue-100 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-5 py-2 flex items-center justify-between text-xs font-semibold text-blue-700 dark:text-blue-300">
						<span>
							Filtered by:{' '}
							{[
								dateFrom && `From ${dateFrom}`,
								dateTo && `To ${dateTo}`,
								service,
								status && STATUS_OPTIONS.find(o => o.key === status)?.label,
							]
								.filter(Boolean)
								.join(' · ')}
						</span>
						<button
							onClick={handleClearFilters}
							className="inline-flex items-center gap-1 underline hover:no-underline"
						>
							<XMarkIcon className="w-3 h-3" /> Clear
						</button>
					</div>
				)}
			</div>

			{/* Stat Cards */}
			{data && (
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
					<TotalEarningsCard
						value={data.totalEarnings}
						changePct={data.totalEarningsChangePct}
					/>
					<ServicesCompletedCard
						value={data.servicesCompleted}
						delta={data.servicesCompletedDelta}
					/>
				</div>
			)}

			{/* Transactions */}
			<div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
				<div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
					<div className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
						<ClipboardDocumentListIcon className="w-4 h-4" />
						Transactions
						{data && (
							<span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-1">
								({data.totalElements})
							</span>
						)}
					</div>
					<button
						type="button"
						onClick={handleDownloadPdf}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
					>
						<DocumentArrowDownIcon className="w-4 h-4" />
						Download PDF
					</button>
				</div>

				{/* Desktop table */}
				<div className="hidden md:block overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr className="bg-gray-50 dark:bg-gray-700/50">
								<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
									Week
								</th>
								<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
									Services
								</th>
								<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
									Your Commission
								</th>
								<th className="px-5 py-3.5 text-left text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
									Status
								</th>
							</tr>
						</thead>
						<tbody>
							{transactions.map((t, idx) => (
								<tr
									key={`${t.weekStart}-${idx}`}
									className="border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
								>
									<td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-200">
										{t.label}
									</td>
									<td className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
										{t.serviceCount}
									</td>
									<td className="px-5 py-4 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
										{formatCurrency(t.commission)}
									</td>
									<td className="px-5 py-4">
										<StatusBadge status={t.status} />
									</td>
								</tr>
							))}
							{transactions.length === 0 && (
								<tr>
									<td
										colSpan={4}
										className="px-5 py-12 text-center text-sm text-gray-400 dark:text-gray-500"
									>
										No transactions match your filters.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{/* Mobile cards */}
				<div className="md:hidden flex flex-col">
					{transactions.map((t, idx) => (
						<div
							key={`${t.weekStart}-${idx}`}
							className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
						>
							<div className="min-w-0 flex-1">
								<div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
									{t.label}
								</div>
								<div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
									{t.serviceCount} service{t.serviceCount === 1 ? '' : 's'}
								</div>
							</div>
							<div className="flex flex-col items-end gap-1 shrink-0 ml-3">
								<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
									{formatCurrency(t.commission)}
								</div>
								<StatusBadge status={t.status} />
							</div>
						</div>
					))}
					{transactions.length === 0 && (
						<div className="px-4 py-12 text-center text-sm text-gray-400 dark:text-gray-500">
							No transactions match your filters.
						</div>
					)}
				</div>

				{data && (
					<div className="px-5 pb-5">
						<Pagination
							currentPage={data.page}
							totalPages={data.totalPages}
							totalElements={data.totalElements}
							pageSize={data.pageSize || PAGE_SIZE}
							onPageChange={setPage}
						/>
					</div>
				)}
			</div>
		</AppLayout>
	);
};

export default Earnings;
